import { useState } from "react";
import { Check, ArrowUpRight, Download, FileSignature } from "lucide-react";
import { EQUITY, COMPANY, NOTE_CALC } from "@/data/content";
import { SectionLabel, Reveal } from "@/components/site/primitives";
import { EmailScenarioBar } from "@/components/site/EmailScenarioBar";

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(
    isFinite(n) ? n : 0
  );
const num = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(isFinite(n) ? n : 0);

const PRESETS = [10000, 50000, 100000, 250000, 1000000, 2500000];

const InvestmentCalculator = () => {
  const [amount, setAmount] = useState(10000);
  const shares = amount > 0 ? amount / EQUITY.priceNum : 0;
  const ownership = EQUITY.fdShares ? (shares / EQUITY.fdShares) * 100 : 0;
  const ownershipStr = `${ownership.toFixed(ownership < 1 ? 3 : 2)}%`;

  const handleInput = (e) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    setAmount(digits ? parseInt(digits, 10) : 0);
  };

  return (
    <Reveal delay={0.1} className="mt-4 md:mt-6 border border-epic-line bg-epic-surface p-6 md:p-10" >
      <div data-testid="equity-calculator">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-accent mb-6">
          Calculate Your Investment
        </p>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end mb-10">
          <div className="md:col-span-7">
            <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub">
              Investment amount
            </label>
            <div className="relative mt-3">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 font-grotesk font-black text-2xl text-epic-sub">$</span>
              <input
                type="text"
                inputMode="numeric"
                data-testid="equity-calc-input"
                value={num(amount)}
                onChange={handleInput}
                className="w-full bg-epic-bg border border-epic-line focus:border-epic-accent outline-none pl-10 pr-4 py-4 font-grotesk font-black text-2xl md:text-3xl text-epic-text tracking-tight transition-colors duration-200"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`equity-calc-preset-${p}`}
                  onClick={() => setAmount(p)}
                  className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-200 ${
                    amount === p ? "border-epic-accent text-epic-accent" : "border-epic-line text-epic-sub hover:border-epic-lineHover"
                  }`}
                >
                  {p >= 1000000 ? `$${p / 1000000}M` : `$${p / 1000}K`}
                </button>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 border-l-0 md:border-l border-epic-line md:pl-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub">
              Shares at {EQUITY.price}
            </p>
            <p data-testid="equity-calc-shares" className="font-grotesk font-black text-4xl md:text-5xl text-epic-text tracking-tighter mt-2">
              {num(Math.floor(shares))}
            </p>
            <p className="font-mono text-[11px] text-epic-sub/60 mt-1">shares purchased</p>
            <div className="mt-5 pt-5 border-t border-epic-line">
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub">
                Approx. Ownership <span className="text-epic-sub/50">(current basis)</span>
              </p>
              <p data-testid="equity-calc-ownership" className="font-grotesk font-black text-3xl md:text-4xl text-epic-accent tracking-tighter mt-2">
                {ownershipStr}
              </p>
            </div>
          </div>
        </div>

        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub mb-4">
          Value at exit share price
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {EQUITY.exitPrices.map((price) => {
            const value = shares * price;
            const multiple = EQUITY.priceNum ? price / EQUITY.priceNum : 0;
            return (
              <div
                key={price}
                data-testid={`equity-calc-exit-${price}`}
                className="border border-epic-line bg-epic-bg p-5 md:p-6 transition-colors duration-300 hover:border-epic-lineHover"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-accent">
                  ${price}.00 / share
                </p>
                <p className="font-grotesk font-black text-2xl md:text-3xl text-epic-text tracking-tighter mt-3 leading-none">
                  {usd(value)}
                </p>
                <p className="font-mono text-[11px] text-epic-sub mt-2">
                  {multiple.toFixed(1)}× return
                </p>
              </div>
            );
          })}
        </div>

        {/* Mirror verdict — same $ in the note, all things equal */}
        {(() => {
          const maxBonus = Math.max(...NOTE_CALC.bonuses.map((b) => b.value));
          const noteShares = (amount / NOTE_CALC.convPrice) * (1 + maxBonus);
          const noteInterest = amount * NOTE_CALC.interestRate * NOTE_CALC.termYears;
          const results = NOTE_CALC.exits.map((s) => {
            const price = (s.equityM * 1e6) / EQUITY.fdShares;
            return { label: s.label, equityWins: shares * price > noteShares * price + noteInterest };
          });
          const eqWins = results.filter((r) => r.equityWins).map((r) => r.label);
          const noteWins = results.filter((r) => !r.equityWins).map((r) => r.label);
          const spread = 1 / EQUITY.priceNum - (1 + maxBonus) / NOTE_CALC.convPrice;
          const breakeven = spread > 0 ? (NOTE_CALC.interestRate * NOTE_CALC.termYears) / spread : null;
          return (
            <div data-testid="equity-verdict" className="mt-6 border border-epic-line bg-epic-bg p-5 md:p-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-accent mb-3">
                Same $ as the note — all things equal
              </p>
              <p className="font-mono text-xs text-epic-sub leading-relaxed">
                Measured against a {usd(amount)} note at its max {maxBonus * 100}% bonus election with
                interest counted:{" "}
                <span className="text-epic-text">
                  {eqWins.length === 3
                    ? "equity wins every modeled scenario"
                    : eqWins.length === 0
                      ? "the note wins every modeled scenario"
                      : `equity wins the ${eqWins.join(" and ")}; the note wins ${noteWins.join(" and ")}`}
                </span>
                .{" "}
                {breakeven
                  ? `Equity pulls ahead only if the exit price beats $${breakeven.toFixed(2)}/share — the modeled scenarios imply $${NOTE_CALC.exits.map((s) => ((s.equityM * 1e6) / EQUITY.fdShares).toFixed(2)).join(" / $")}.`
                  : "The note's yield wins at every modeled exit price."}
              </p>
            </div>
          );
        })()}

        {/* Email capture */}
        <EmailScenarioBar endpoint="/api/equity-scenario" payload={{ amount }} testId="equity-calc" />

        <p className="font-mono text-[11px] text-epic-sub/50 mt-6">
          Illustrative only. Assumes purchase at {EQUITY.price}/share on a current fully-diluted basis of {num(EQUITY.fdShares)} shares; ownership % will be diluted as new shares are issued in this raise. Figures exclude fees, dilution, and taxes. Not a guarantee of any return.
        </p>
      </div>
    </Reveal>
  );
};

export const EquityOption = () => {
  return (
    <section
      id="equity"
      data-testid="equity-section"
      className="mx-auto max-w-[1400px] px-5 md:px-10 py-28 md:py-44"
    >
      <SectionLabel index="[ 06 ]" testId="equity-label">
        Alternative — Direct Equity
      </SectionLabel>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-stretch">
        {/* Copy + points */}
        <Reveal className="md:col-span-7 border border-epic-line bg-epic-surface p-6 md:p-10">
          <h2 className="font-grotesk font-black uppercase tracking-tighter text-4xl md:text-5xl lg:text-6xl text-epic-text leading-[0.9] mb-6">
            Prefer equity?<br />
            <span className="text-epic-accent">Skip</span> the note.
          </h2>
          <p className="font-mono text-sm md:text-base text-epic-sub leading-relaxed max-w-xl mb-8">
            For investors who would rather own shares outright, ePIC offers a direct equity
            investment as an alternative to the {COMPANY.instrument} — priced at{" "}
            {EQUITY.price} per share.
          </p>
          <ul className="space-y-4">
            {EQUITY.points.map((p, i) => (
              <li key={i} className="flex gap-4" data-testid={`equity-point-${i}`}>
                <Check className="w-4 h-4 text-epic-accent shrink-0 mt-1" strokeWidth={3} />
                <span className="font-mono text-sm text-epic-text leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        {/* Price card */}
        <Reveal delay={0.1} className="md:col-span-5 border border-epic-line bg-epic-accent p-6 md:p-10 flex flex-col justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/70">
            Direct Equity — Share Price
          </p>
          <div className="mt-8">
            <p className="font-grotesk font-black text-7xl md:text-8xl text-white leading-none tracking-tighter">
              {EQUITY.price}
            </p>
            <p className="font-mono text-xs text-white/80 mt-3">
              per share · vs {EQUITY.notePrice} note conversion price
            </p>
          </div>
          <div className="mt-8 pt-6 border-t border-white/20 space-y-3 font-mono text-xs text-white">
            <div className="flex justify-between"><span className="text-white/70">Example</span><span>{EQUITY.example.amount}</span></div>
            <div className="flex justify-between"><span className="text-white/70">Buys</span><span>{EQUITY.example.shares}</span></div>
            <div className="flex justify-between"><span className="text-white/70">Minimum</span><span>{EQUITY.minTicket}</span></div>
          </div>
        </Reveal>
      </div>

      <InvestmentCalculator />

      <Reveal delay={0.15} className="mt-6 border border-epic-line bg-epic-bg px-6 py-10 md:px-10 md:py-14">
        {(() => {
          const minX = Math.min(...EQUITY.exitPrices) / EQUITY.priceNum;
          const maxX = Math.max(...EQUITY.exitPrices) / EQUITY.priceNum;
          return (
            <>
              <p
                data-testid="exit-callout"
                className="font-grotesk font-black uppercase tracking-tighter text-3xl md:text-5xl lg:text-6xl leading-[0.95] text-epic-text"
              >
                {EQUITY.price} in.{" "}
                <span className="text-epic-accent">Up to {maxX.toFixed(0)}× out.</span>
              </p>
              <p className="font-mono text-sm text-epic-sub leading-relaxed mt-5 max-w-3xl">
                At the modeled exit share prices of ${Math.min(...EQUITY.exitPrices)}–$
                {Math.max(...EQUITY.exitPrices)}, every share purchased at {EQUITY.price} carries a{" "}
                {minX.toFixed(1)}×–{maxX.toFixed(1)}× return multiple. Illustrative only — not a
                guarantee of any return.
              </p>
            </>
          );
        })()}
      </Reveal>

      <Reveal delay={0.1} className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
        {EQUITY.signUrl && (
          <a
            href={EQUITY.signUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="equity-sign-online"
            className="group inline-flex items-center gap-3 bg-epic-accent px-8 py-5 text-white font-mono text-xs uppercase tracking-[0.15em] transition-[background-color,transform] duration-300 hover:bg-epic-accentHover active:scale-95"
          >
            <FileSignature className="w-4 h-4" />
            Sign the Agreement Online
          </a>
        )}
        <a
          href={EQUITY.docUrl}
          download
          target="_blank"
          rel="noopener noreferrer"
          data-testid="equity-download-agreement"
          className="group inline-flex items-center gap-3 border border-epic-line px-8 py-5 text-epic-text font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-epic-lineHover"
        >
          <Download className="w-4 h-4" />
          Download Stock Purchase Agreement
        </a>
        <a
          href={COMPANY.meetingUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="equity-book-meeting"
          className="group inline-flex items-center gap-3 border border-epic-line px-8 py-5 text-epic-text font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-epic-lineHover"
        >
          Discuss the Equity Option
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </Reveal>
    </section>
  );
};
